import { 
  collection, 
  addDoc, 
  query, 
  where,
  orderBy,
  limit,
  getDocs,
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore';
import { db } from './config';

const COLLECTION_NAME = 'activityLogs';

/**
 * Log an activity
 * This function never throws errors - logging failures won't break operations
 */
export const logActivity = async ({
  userId,
  userEmail,
  userName,
  action,
  entityType,
  entityId,
  entityName,
  details = {},
  metadata = {}
}) => {
  try {
    // Filter out undefined values from details and metadata
    const cleanDetails = Object.entries(details).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null) {
        acc[key] = value;
      }
      return acc;
    }, {});
    
    const cleanMetadata = Object.entries(metadata).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null) {
        acc[key] = value;
      }
      return acc;
    }, {});

    await addDoc(collection(db, COLLECTION_NAME), {
      userId: userId || '',
      userEmail: userEmail || '',
      userName: userName || userEmail || '',
      action: action || '',
      entityType: entityType || 'system',
      entityId: entityId || '',
      entityName: entityName || '',
      details: cleanDetails,
      metadata: cleanMetadata,
      timestamp: serverTimestamp(),
      createdAt: new Date().toISOString()
    });
  } catch (error) {
    // Log to console but don't throw - logging failures shouldn't break operations
    console.error('Failed to log activity:', error);
  }
};

/**
 * Subscribe to activity logs with real-time updates
 * entityType filtering is done in-memory to avoid composite index requirement
 */
export const subscribeToActivityLogs = (callback, options = {}) => {
  try {
    const { 
      limitCount = 100, 
      userId = null, 
      startDate = null,
      endDate = null
      // entityType intentionally NOT used in query — filtered in-memory by caller
    } = options;
    
    let constraints = [orderBy('createdAt', 'desc')];

    // Date range filter (uses single-field index on createdAt — no composite needed)
    if (startDate) {
      constraints.push(where('createdAt', '>=', startDate));
    }
    if (endDate) {
      constraints.push(where('createdAt', '<=', endDate));
    }

    // userId filter only — combining with entityType would need a composite index
    if (userId) {
      constraints.push(where('userId', '==', userId));
    }

    constraints.push(limit(limitCount));

    const q = query(collection(db, COLLECTION_NAME), ...constraints);

    return onSnapshot(q, (snapshot) => {
      const logs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate?.() || new Date(doc.data().createdAt)
      }));
      callback(logs);
    }, (error) => {
      console.error('Error subscribing to activity logs:', error);
      callback([]);
    });
  } catch (error) {
    console.error('Error setting up activity logs subscription:', error);
    return () => {};
  }
};

/**
 * Get activity logs by date range
 */
export const getActivityLogsByDateRange = async (startDate, endDate, options = {}) => {
  try {
    const { userId = null, entityType = null, action = null } = options;
    
    let q = query(
      collection(db, COLLECTION_NAME),
      where('createdAt', '>=', startDate),
      where('createdAt', '<=', endDate),
      orderBy('createdAt', 'desc')
    );

    if (userId) {
      q = query(q, where('userId', '==', userId));
    }
    if (entityType) {
      q = query(q, where('entityType', '==', entityType));
    }
    if (action) {
      q = query(q, where('action', '==', action));
    }

    const snapshot = await getDocs(q);
    const logs = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp?.toDate?.() || new Date(doc.data().createdAt)
    }));

    return { success: true, data: logs };
  } catch (error) {
    console.error('Error getting activity logs by date range:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Get activity logs by user
 */
export const getActivityLogsByUser = async (userId, limitCount = 50) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('userId', '==', userId),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    );

    const snapshot = await getDocs(q);
    const logs = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp?.toDate?.() || new Date(doc.data().createdAt)
    }));

    return { success: true, data: logs };
  } catch (error) {
    console.error('Error getting activity logs by user:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Get activity logs by entity
 */
export const getActivityLogsByEntity = async (entityType, entityId) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('entityType', '==', entityType),
      where('entityId', '==', entityId),
      orderBy('timestamp', 'desc')
    );

    const snapshot = await getDocs(q);
    const logs = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp?.toDate?.() || new Date(doc.data().createdAt)
    }));

    return { success: true, data: logs };
  } catch (error) {
    console.error('Error getting activity logs by entity:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Get recent activity logs
 */
export const getRecentActivityLogs = async (limitCount = 50) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    );

    const snapshot = await getDocs(q);
    const logs = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp?.toDate?.() || new Date(doc.data().createdAt)
    }));

    return { success: true, data: logs };
  } catch (error) {
    console.error('Error getting recent activity logs:', error);
    return { success: false, error: error.message, data: [] };
  }
};

// Action types constants for consistency
export const ACTIONS = {
  // Inventory
  CREATED_INVENTORY_ITEM: 'created_inventory_item',
  UPDATED_INVENTORY_ITEM: 'updated_inventory_item',
  DELETED_INVENTORY_ITEM: 'deleted_inventory_item',
  UPDATED_INVENTORY_PRICING: 'updated_inventory_pricing',
  RESTOCKED_ITEM: 'restocked_item',

  // GRN
  CREATED_GRN: 'created_grn',
  UPDATED_GRN: 'updated_grn',
  DELETED_GRN: 'deleted_grn',

  // Stock Transfer
  CREATED_STOCK_TRANSFER: 'created_stock_transfer',

  // Purchase Orders
  CREATED_PURCHASE_ORDER: 'created_purchase_order',
  UPDATED_PURCHASE_ORDER: 'updated_purchase_order',
  DELETED_PURCHASE_ORDER: 'deleted_purchase_order',
  CANCELLED_PURCHASE_ORDER: 'cancelled_purchase_order',

  // Suppliers
  CREATED_SUPPLIER: 'created_supplier',
  UPDATED_SUPPLIER: 'updated_supplier',
  DELETED_SUPPLIER: 'deleted_supplier',

  // Trade Payables / Receivables
  CREATED_TRADE_PAYABLE: 'created_trade_payable',
  UPDATED_TRADE_PAYABLE: 'updated_trade_payable',
  DELETED_TRADE_PAYABLE: 'deleted_trade_payable',
  RECORDED_PAYMENT: 'recorded_payment',
  UPDATED_PAYMENT: 'updated_payment',
  DELETED_PAYMENT: 'deleted_payment',

  // Purchase Invoice (legacy)
  CREATED_PURCHASE_INVOICE: 'created_purchase_invoice',
  UPDATED_PURCHASE_INVOICE: 'updated_purchase_invoice',
  DELETED_PURCHASE_INVOICE: 'deleted_purchase_invoice',
  
  // Billing
  CREATED_BILL: 'created_bill',
  UPDATED_BILL: 'updated_bill',
  DELETED_BILL: 'deleted_bill',
  
  // Customers
  CREATED_CUSTOMER: 'created_customer',
  UPDATED_CUSTOMER: 'updated_customer',
  DELETED_CUSTOMER: 'deleted_customer',
  
  // Expenses
  CREATED_EXPENSE: 'created_expense',
  UPDATED_EXPENSE: 'updated_expense',
  DELETED_EXPENSE: 'deleted_expense',
  CREATED_EXPENSE_CATEGORY: 'created_expense_category',
  UPDATED_EXPENSE_CATEGORY: 'updated_expense_category',
  ARCHIVED_EXPENSE_CATEGORY: 'archived_expense_category',
  
  // Creditors
  CREATED_CREDITOR: 'created_creditor',
  UPDATED_CREDITOR: 'updated_creditor',
  DELETED_CREDITOR: 'deleted_creditor',
  
  // Returns
  CREATED_RETURN: 'created_return',
  
  // Staff
  CREATED_STAFF: 'created_staff',
  UPDATED_STAFF: 'updated_staff',
  DELETED_STAFF: 'deleted_staff',
  UPDATED_PERMISSIONS: 'updated_permissions',
  
  // Settings
  UPDATED_SETTINGS: 'updated_settings',
  
  // Auth
  USER_LOGIN: 'user_login',
  USER_LOGOUT: 'user_logout',
  PASSWORD_RESET_REQUESTED: 'password_reset_requested',
};

// Entity types constants
export const ENTITY_TYPES = {
  INVENTORY: 'inventory',
  GRN: 'grn',
  STOCK_TRANSFER: 'stock_transfer',
  PURCHASE_INVOICE: 'purchase_invoice',
  PURCHASE_ORDER: 'purchase_order',
  SUPPLIER: 'supplier',
  TRADE_PAYABLE: 'trade_payable',
  TRADE_RECEIVABLE: 'trade_receivable',
  CASH_BANK: 'cash_bank',
  BILL: 'bill',
  CUSTOMER: 'customer',
  EXPENSE: 'expense',
  EXPENSE_CATEGORY: 'expense_category',
  CREDITOR: 'creditor',
  STAFF: 'staff',
  SETTINGS: 'settings',
  AUTH: 'auth',
  RETURN: 'return',
  RESTOCK: 'restock',
};
